import { useMemo, useState } from 'react';
import type { MapDocument } from '@orbit-mind/domain';
import type { Scene } from '@orbit-mind/map-engine';
import {
  EXPORT_FORMATS,
  exportFilename,
  renderText,
  renderFreemind,
  renderNativeJson,
  renderMarkdown,
  sceneToSvg,
  exportPreflight,
  type AdvancedExportFormat,
} from '@orbit-mind/export-engine';
import { sceneToPng } from '@orbit-mind/export-engine/browser';
import { useLocale } from '@orbit-mind/engine-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/controls';
import { saveBlob } from './useExport';

export function AdvancedExport({
  open,
  onClose,
  document,
  scene,
}: {
  open: boolean;
  onClose: () => void;
  document: MapDocument;
  scene: Scene;
}): React.JSX.Element {
  const { text: t } = useLocale();
  const [format, setFormat] = useState<AdvancedExportFormat>('png');
  const [scale, setScale] = useState<1 | 2 | 4>(2);
  const [transparent, setTransparent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pagination, setPagination] = useState<'fit' | 'tile'>('fit');
  const preflight = useMemo(
    () => exportPreflight(scene, scale, { mode: pagination }),
    [scene, scale, pagination],
  );
  const thumbnail = useMemo(
    () =>
      `data:image/svg+xml;charset=utf-8,${encodeURIComponent(sceneToSvg(scene, { title: document.map.title }))}`,
    [scene, document.map.title],
  );
  const [notice, setNotice] = useState('');
  async function download(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setNotice('');
    try {
      let blob: Blob;
      const title = document.map.title;
      if (format === 'png' || format === 'jpg') {
        const result = await sceneToPng(scene, {
          title,
          scale,
          background: format === 'jpg' ? '#ffffff' : transparent ? null : '#ffffff',
          mimeType: format === 'jpg' ? 'image/jpeg' : 'image/png',
        });
        if (!result.ok) throw new Error(result.error.message);
        blob = result.value.blob;
        if (result.value.size.clamped)
          setNotice(
            t(
              'Image resized to fit device limits.',
              'Imagem redimensionada para respeitar os limites do dispositivo.',
            ),
          );
      } else if (format === 'pdf' || format === 'report') {
        const pdf = await import('@orbit-mind/export-engine/pdf-unicode');
        const bytes =
          format === 'report'
            ? await pdf.documentToReportPdf(document)
            : (await pdf.sceneToUnicodePdf(scene, { title, pagination: { mode: pagination } }))
                .bytes;
        blob = new Blob([bytes as BlobPart], { type: 'application/pdf' });
      } else {
        const content =
          format === 'md'
            ? renderMarkdown(document)
            : format === 'txt'
              ? renderText(document)
              : format === 'mm'
                ? renderFreemind(document)
                : format === 'json'
                  ? renderNativeJson(document)
                  : sceneToSvg(scene, { title });
        blob = new Blob([content], {
          type:
            format === 'svg'
              ? 'image/svg+xml'
              : format === 'json'
                ? 'application/json'
                : format === 'mm'
                  ? 'application/xml'
                  : 'text/plain;charset=utf-8',
        });
      }
      saveBlob(blob, exportFilename(title, format === 'report' ? 'pdf' : format));
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : t('Export failed.', 'Falha ao exportar.'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open={open}
      onOpenChange={(value) => {
        if (!value && !busy) onClose();
      }}
      title={t('Advanced export', 'Exportação avançada')}
      size="narrow"
    >
      <div className="space-y-5 overflow-y-auto p-5">
        <img
          src={thumbnail}
          alt={t('Map export preview', 'Prévia do mapa exportado')}
          className="border-edge max-h-40 w-full rounded-xl border bg-white object-contain"
        />
        <div className="grid grid-cols-2 gap-2">
          {EXPORT_FORMATS.map((item) => (
            <Button
              key={item.id}
              tone={format === item.id ? 'primary' : 'ghost'}
              onClick={() => {
                setFormat(item.id);
              }}
            >
              {item.label}
            </Button>
          ))}
        </div>
        {format === 'png' || format === 'jpg' ? (
          <>
            <fieldset>
              <legend>{t('Resolution', 'Resolução')}</legend>
              <div className="mt-2 flex gap-3">
                {([1, 2, 4] as const).map((value) => (
                  <Button
                    key={value}
                    tone={scale === value ? 'primary' : 'ghost'}
                    onClick={() => {
                      setScale(value);
                    }}
                  >
                    {value}×
                  </Button>
                ))}
              </div>
            </fieldset>
            {format === 'png' && (
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={transparent}
                  onChange={(event) => {
                    setTransparent(event.target.checked);
                  }}
                />
                {t('Transparent background', 'Fundo transparente')}
              </label>
            )}
          </>
        ) : (
          <p className="text-ink-muted text-sm">
            {t(
              'Vector or text output: resolution independent.',
              'Saída vetorial ou em texto: independente de resolução.',
            )}
          </p>
        )}
        {(format === 'png' || format === 'jpg') && (
          <p role="status" className="text-sm">
            {preflight.raster.width} × {preflight.raster.height} px ·{' '}
            {t('bitmap memory estimate', 'estimativa de memória do bitmap')}: {preflight.bitmapMiB}{' '}
            MiB
            {preflight.raster.clamped
              ? t(
                  ' · Resolution will be reduced to fit device limits.',
                  ' · A resolução será reduzida para respeitar os limites do aparelho.',
                )
              : ''}
            {preflight.largeImage
              ? t(
                  ' · Large image: use 1× or SVG for lighter sharing.',
                  ' · Imagem grande: use 1× ou SVG para compartilhar com menos memória.',
                )
              : ''}
          </p>
        )}
        {format === 'pdf' && (
          <fieldset className="space-y-2">
            <legend>{t('PDF pages', 'Páginas do PDF')}</legend>
            <div className="flex gap-2">
              {(['fit', 'tile'] as const).map((mode) => (
                <Button
                  key={mode}
                  tone={pagination === mode ? 'primary' : 'ghost'}
                  onClick={() => {
                    setPagination(mode);
                  }}
                >
                  {mode === 'fit'
                    ? t('One page', 'Uma página')
                    : t('Pages at readable size', 'Páginas em tamanho legível')}
                </Button>
              ))}
            </div>
            <p role="status">
              {preflight.pdfError ||
                t(
                  `Preview: ${String(preflight.pages)} A4 page(s), left to right, top to bottom.`,
                  `Prévia: ${String(preflight.pages)} página(s) A4, da esquerda para a direita, de cima para baixo.`,
                )}
            </p>
            {pagination === 'fit' && preflight.smallText && (
              <p>
                {t(
                  'Text will be small. Choose multiple pages or a PDF report.',
                  'O texto ficará pequeno. Escolha várias páginas ou um relatório PDF.',
                )}
              </p>
            )}
          </fieldset>
        )}
        {format === 'json' && (
          <p className="text-ink-muted text-sm">
            {t(
              'Editable map. Attached files must be saved separately.',
              'Mapa editável. Os arquivos anexados devem ser salvos separadamente.',
            )}
          </p>
        )}
        <Button
          tone="primary"
          disabled={busy || (format === 'pdf' && preflight.pdfError !== '')}
          onClick={() => {
            void download();
          }}
        >
          {busy ? t('Preparing…', 'Preparando…') : t('Export file', 'Exportar arquivo')}
        </Button>
        {notice && (
          <p role="status" className="text-sm">
            {notice}
          </p>
        )}
      </div>
    </Modal>
  );
}
